import {
  ChangeDetectorRef,
  Component,
  ComponentRef,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzStepsModule } from 'ng-zorro-antd/steps';
import { finalize, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { FeedbackService } from '../feedback.service';
import { LoaderService } from '../../../../../core/services/loader.service';
import { CommonModule } from '@angular/common';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { DrawerService } from '../../../../../core/services/drawer.service';
import { DRAWER_IDS } from '../../../../../core/constants/drawer-ids.constant';
import { CommonService } from '../../../../../shared/services/common.service';
import { AuthService } from '../../../../../core/services/auth.service';
import {
  FeedBackByCase,
  FeedbackDrawerParams,
  FeedbackQuestion,
  QuestionsRes,
} from '../feedback';
import { ApiResponse } from '../../../../../core/models/auth.models';
import { InsightMode } from '../../../insights.enums';
import {
  FeedbackStepAnswer,
  FeedbackStepData,
  StepValidation,
  EnhancedFeedbackQuestion,
  PrintTemplateData,
} from './feedback-step.interface';
import { GeneralBehaviorStepComponent } from './steps/general-behavior-step/general-behavior-step.component';
import { BehaviorMetricsStepComponent } from './steps/behavior-metrics-step/behavior-metrics-step.component';
import { InterventionSupportStepComponent } from './steps/intervention-support-step/intervention-support-step.component';
import { StakeholderFeedbackStepComponent } from './steps/stakeholder-feedback-step/stakeholder-feedback-step.component';
import { SelfAssessmentStepComponent } from './steps/self-assessment-step/self-assessment-step.component';
import { QuestionCategory, SubQuestionCategory } from '../feeback.enum';
import { QuestionType, QUESTION_TYPE_MAP } from './feedback-question.enum';
import { ErrorResponse } from '../../../../../core/models/error.models';
import { OrganizationService } from '../../../../organizations/organization.service';
import { PrintTemplateComponent } from './steps/self-assessment-step/print-template/print-template.component';
import { OrganizationModel } from '../../../../organizations/organization.interface';

@Component({
  selector: 'app-feedback-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzButtonModule,
    NzSpinModule,
    NzStepsModule,
    GeneralBehaviorStepComponent,
    BehaviorMetricsStepComponent,
    InterventionSupportStepComponent,
    StakeholderFeedbackStepComponent,
    SelfAssessmentStepComponent,
  ],
  templateUrl: './feedback-form.component.html',
  styleUrl: './feedback-form.component.scss',
})
export class FeedbackFormComponent implements OnInit, OnDestroy {
  private readonly drawerService = inject(DrawerService);
  private readonly fb = inject(FormBuilder);
  private readonly loaderService = inject(LoaderService);
  private readonly authService = inject(AuthService);
  private readonly feedbackService = inject(FeedbackService);
  private readonly commonService = inject(CommonService);
  private injector = inject(Injector);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly orgService = inject(OrganizationService);
  private cd = inject(ChangeDetectorRef);

  @ViewChild(SelfAssessmentStepComponent)
  selfAssessmentStepComponent!: SelfAssessmentStepComponent;

  feedbackForm: FormGroup;
  questions: FeedbackQuestion[] = [];
  private destroy$ = new Subject<void>();
  isPrintLoading = false;
  feedbackDetails = {} as FeedbackDrawerParams;
  pendingFeedbackData: FeedBackByCase | null = null;
  overAllScore = {
    GeneralBehaviourRating: 0,
    BehaviorSpecificMetrics: 0,
    InterventionAndSupportFeedback: 0,
    StakeholderFeedback: 0,
    SelfAssessment: 0,
  };
  currentStep = 0;
  totalOverAllScore = 0;
  steps = [
    {
      title: 'General Behavior Rating',
      description: 'Overall behavior assessment',
    },
    {
      title: 'Behavior-Specific Metrics',
      description: 'Detailed behavior analysis',
    },
    {
      title: 'Intervention & Support',
      description: 'Support needs and modifications',
    },
    {
      title: 'Stakeholder Feedback',
      description: 'Progress and parent feedback',
    },
    { title: 'Self-Assessment', description: 'Teacher and student reflection' },
  ];
  stepData: Record<number, FeedbackStepData> = {};
  isStepDataInitialized = false;

  loading$: Observable<boolean> = of(false);
  hasAddedFeedback = false;
  loggedInUserId = 0;

  constructor() {
    this.loading$ = this.loaderService.loading$;
    this.authService.userData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.loggedInUserId = user?.id || 0;
      });

    this.getQuestions();
    this.feedbackForm = this.fb.group({
      caseFeedbackSubmissionId: [0],
      organizationId: [0, Validators.required],
      caseId: [0, Validators.required],
      caseBehaviorMappingId: [null],
      questionResponses: this.fb.array([]),
    });
  }

  ngOnInit(): void {
    this.getDrawerParams();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getDrawerParams(): void {
    this.drawerService
      .getParams<FeedbackDrawerParams>(DRAWER_IDS.FEEDBACK_FORM)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.feedbackDetails = params;
          this.hasAddedFeedback =
            params.data?.caseMemberUserId === this.loggedInUserId;
          if (params.data) {
            this.getFeedbackByCaseId();
            this.feedbackForm.patchValue({
              caseFeedbackSubmissionId: params.data.id || 0,
              caseId: params.caseDetail.id || 0,
              organizationId: params.caseDetail.student.organizationId || 0,
            });
          }
        }
      });
  }
  getFeedbackByCaseId(): void {
    if (
      this.feedbackDetails.data &&
      'questionResponses' in this.feedbackDetails.data
    ) {
      const feedbackData = this.feedbackDetails
        .data as unknown as FeedBackByCase;
      if (feedbackData.questionResponses) {
        let allQuestionResponses: QuestionsRes[] = [];
        const categoryKeys = Object.keys(feedbackData.questionResponses);

        for (const categoryKey of categoryKeys) {
          let mappedCategoryKey = categoryKey;
          const enumKeys = Object.keys(QuestionCategory);
          const foundEnumKey = enumKeys.find(key => key === categoryKey);

          if (
            foundEnumKey &&
            this.feedbackDetails.mode === InsightMode.RESUME
          ) {
            mappedCategoryKey = foundEnumKey;
            this.currentStep =
              QuestionCategory[categoryKey as keyof typeof QuestionCategory] -
              1;
          }
          const categoryData = (
            feedbackData.questionResponses as Record<string, unknown>
          )[categoryKey];

          if (
            categoryData &&
            typeof categoryData === 'object' &&
            'overallScore' in categoryData &&
            typeof (categoryData as { overallScore: number }).overallScore ===
              'number'
          ) {
            const score = (categoryData as { overallScore: number })
              .overallScore;
            if (mappedCategoryKey in this.overAllScore) {
              (this.overAllScore as Record<string, number>)[mappedCategoryKey] =
                score;
            }
          }
        }

        this.totalOverAllScore = feedbackData.overallScore || 0;

        if (Array.isArray(feedbackData.questionResponses)) {
          allQuestionResponses = feedbackData.questionResponses;
        } else {
          Object.keys(feedbackData.questionResponses).forEach(categoryKey => {
            const categoryData = (
              feedbackData.questionResponses as Record<string, unknown>
            )[categoryKey];
            if (
              categoryData &&
              typeof categoryData === 'object' &&
              'overallScore' in categoryData &&
              typeof (categoryData as { overallScore: number }).overallScore ===
                'number'
            ) {
              const score = (categoryData as { overallScore: number })
                .overallScore;
              if (categoryKey in this.overAllScore) {
                (this.overAllScore as Record<string, number>)[categoryKey] =
                  score;
              }
            }
            if (
              categoryData &&
              typeof categoryData === 'object' &&
              'questionResponses' in categoryData &&
              Array.isArray(
                (categoryData as { questionResponses: QuestionsRes[] })
                  .questionResponses
              )
            ) {
              allQuestionResponses = allQuestionResponses.concat(
                (categoryData as { questionResponses: QuestionsRes[] })
                  .questionResponses
              );
            }
          });
        }

        const mappedResponses = allQuestionResponses.map(
          (response: QuestionsRes) => ({
            id: response.id,
            caseFeedbackQuestionId: response.caseFeedbackQuestionId,
            ratingScale: response.ratingScale,
            description: response.answerText || '',
            caseBehaviorMappingId: response.caseBehaviorMappingId || null,
          })
        );

        this.feedbackForm.patchValue({
          id: feedbackData.id,
          caseId: feedbackData.caseId,
          organizationId: feedbackData.organizationId,
          caseBehaviorMappingId:
            mappedResponses[6]?.caseBehaviorMappingId || null,
        });

        const questionResponsesArray = this.feedbackForm.get(
          'questionResponses'
        ) as FormArray;
        questionResponsesArray.clear();

        if (this.questions && this.questions.length > 0) {
          this.questions.forEach(question => {
            const existingResponse = mappedResponses.find(
              response => response.caseFeedbackQuestionId === question.id
            );

            const responseGroup = this.fb.group({
              caseFeedbackQuestionId: [question.id],
              ratingScale: [existingResponse?.ratingScale || 0],
              description: [existingResponse?.description || ''],
              caseBehaviorMappingId: [
                existingResponse?.caseBehaviorMappingId || null,
              ],
            });
            questionResponsesArray.push(responseGroup);
          });
        } else {
          this.pendingFeedbackData = feedbackData;
          return;
        }

        this.toggleDisable();

        this.updateStepDataWithFormReferences();
        return;
      }
    }
  }

  updateStepDataWithFormReferences(): void {
    const formArray = this.feedbackForm.get('questionResponses') as FormArray;

    Object.keys(this.stepData).forEach(stepKey => {
      const stepIndex = Number(stepKey);
      const step = this.stepData[stepIndex];
      if (step) {
        step.formArray = formArray;
        const categoryKey = this.getCategoryKeyByStep(stepIndex);
        if (
          categoryKey &&
          this.overAllScore[categoryKey as keyof typeof this.overAllScore]
        ) {
          step.overallScore =
            this.overAllScore[categoryKey as keyof typeof this.overAllScore];
        }
      }
    });
  }

  getCategoryKeyByStep(stepIndex: number): string | null {
    const mappings = [
      { step: 0, key: 'GeneralBehaviourRating' },
      { step: 1, key: 'BehaviorSpecificMetrics' },
      { step: 2, key: 'InterventionAndSupportFeedback' },
      { step: 3, key: 'StakeholderFeedback' },
      { step: 4, key: 'SelfAssessment' },
    ];

    const mapping = mappings.find(m => m.step === stepIndex);
    return mapping ? mapping.key : null;
  }

  getFilteredQuestionsForTab(tabIndex: number): EnhancedFeedbackQuestion[] {
    const currentStepData = this.getCurrentStepData();
    if (!currentStepData?.questions || this.currentStep !== 4) {
      return [];
    }

    let filteredQuestions: FeedbackQuestion[] = [];

    switch (tabIndex) {
      case 0:
        filteredQuestions = currentStepData.questions.filter(
          q => q.subCategoryId === SubQuestionCategory.TeacherReflection
        );
        break;
      case 1:
        filteredQuestions = currentStepData.questions.filter(
          q => q.subCategoryId === SubQuestionCategory.StudentSelfAssessment
        );
        break;
      case 2:
        filteredQuestions = currentStepData.questions.filter(
          q =>
            q.subCategoryId === SubQuestionCategory.TeacherReflection ||
            q.subCategoryId === SubQuestionCategory.StudentSelfAssessment
        );
        break;
      default:
        filteredQuestions = currentStepData.questions;
    }

    return filteredQuestions.map(question => ({
      ...question,
      questionType: this.getQuestionType(question),
      scaleOptions: ['Never', 'Sometimes', 'Often', 'Almost Always'],
    }));
  }

  toggleDisable() {
    const questionArray = this.feedbackForm.get(
      'questionResponses'
    ) as FormArray;
    if (this.isViewMode) {
      questionArray.disable();
    } else {
      questionArray.enable();
    }
  }
  enableEdit() {
    this.feedbackDetails.mode = InsightMode.EDIT;
    this.toggleDisable();
  }

  get isViewMode(): boolean {
    return this.feedbackDetails.mode === InsightMode.VIEW;
  }
  getQuestions(): void {
    this.loaderService.show();
    this.feedbackService
      .getFeedbackQuestionsList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ApiResponse<FeedbackQuestion[]>) => {
          if (response.result) {
            this.questions = response.result;
            const questionsByCategory = this.questions.reduce(
              (acc, question) => {
                const category = question.category;
                if (!acc[category]) {
                  acc[category] = [];
                }
                acc[category].push(question);
                return acc;
              },
              {} as Record<string, FeedbackQuestion[]>
            );

            this.createStepData(questionsByCategory);
            this.buildForm();
            this.getDrawerParams();
          }
        },
        error: error => {
          console.error('Error fetching feedback questions:', error);
          this.commonService.notify(
            'error',
            'Failed to load feedback questions. Using cached questions.'
          );
          this.loaderService.hide();
        },
        complete: () => {
          this.loaderService.hide();
        },
      });
  }

  refreshQuestions(): void {
    this.getQuestions();
  }

  createStepData(
    questionsByCategory: Record<string, FeedbackQuestion[]>
  ): void {
    const categoryMappings = [
      {
        key: 'GeneralBehaviourRating',
        title: 'General Behavior Rating',
        step: 0,
      },
      {
        key: 'BehaviorSpecificMetrics',
        title: 'Behavior-Specific Metrics',
        step: 1,
      },
      {
        key: 'InterventionAndSupportFeedback',
        title: 'Intervention & Support Feedback',
        step: 2,
      },
      { key: 'StakeholderFeedback', title: 'Stakeholder Feedback', step: 3 },
      { key: 'SelfAssessment', title: 'Self-Assessment', step: 4 },
    ];

    categoryMappings.forEach(mapping => {
      const questions = questionsByCategory[mapping.key] || [];
      this.stepData[mapping.step] = {
        questions,
        formArray: this.fb.array([]) as FormArray,
        isViewMode: this.isViewMode,
        categoryTitle: mapping.title,
        overallScore: 15,
      };
    });

    this.isStepDataInitialized = true;
  }

  buildForm(): void {
    const formArray = this.fb.array(
      this.questions.map(question => {
        const questionGroup = this.fb.group({
          caseFeedbackQuestionId: [question.id, Validators.required],
          ratingScale: [0, Validators.required],
          description: [''],
          caseBehaviorMappingId: [null],
        });
        return questionGroup;
      })
    );

    this.feedbackForm.setControl('questionResponses', formArray);
    this.feedbackForm.patchValue({
      organizationId: this.feedbackDetails.student?.organizationId || 0,
      caseId: this.feedbackDetails.caseId || 0,
    });
    Object.keys(this.stepData).forEach(stepKey => {
      const stepIndex = Number(stepKey);
      this.stepData[stepIndex].formArray = formArray;
    });

    if (this.pendingFeedbackData) {
      this.processPendingFeedbackData();
    }
  }

  processPendingFeedbackData(): void {
    if (!this.pendingFeedbackData) return;

    const feedbackData = this.pendingFeedbackData;
    let allQuestionResponses: QuestionsRes[] = [];
    if (Array.isArray(feedbackData.questionResponses)) {
      allQuestionResponses = feedbackData.questionResponses;
    } else {
      Object.keys(feedbackData.questionResponses).forEach(categoryKey => {
        const categoryData = (
          feedbackData.questionResponses as Record<string, unknown>
        )[categoryKey];
        if (
          categoryData &&
          typeof categoryData === 'object' &&
          'overallScore' in categoryData &&
          typeof (categoryData as { overallScore: number }).overallScore ===
            'number'
        ) {
          const score = (categoryData as { overallScore: number }).overallScore;
          if (categoryKey in this.overAllScore) {
            (this.overAllScore as Record<string, number>)[categoryKey] = score;
          }
        }
        if (
          categoryData &&
          typeof categoryData === 'object' &&
          'questionResponses' in categoryData &&
          Array.isArray(
            (categoryData as { questionResponses: QuestionsRes[] })
              .questionResponses
          )
        ) {
          allQuestionResponses = allQuestionResponses.concat(
            (categoryData as { questionResponses: QuestionsRes[] })
              .questionResponses
          );
        }
      });
    }

    const mappedResponses = allQuestionResponses.map(
      (response: QuestionsRes) => ({
        caseFeedbackQuestionId: response.caseFeedbackQuestionId,
        ratingScale: response.ratingScale,
        description: response.answerText || '',
        caseBehaviorMappingId: response.caseBehaviorMappingId || null,
      })
    );
    this.feedbackForm.patchValue({
      id: feedbackData.id,
      caseId: feedbackData.caseId,
      organizationId: feedbackData.organizationId,
    });

    const questionResponsesArray = this.feedbackForm.get(
      'questionResponses'
    ) as FormArray;

    this.questions.forEach((question, questionIndex) => {
      const existingResponse = mappedResponses.find(
        response => response.caseFeedbackQuestionId === question.id
      );

      if (existingResponse && questionIndex < questionResponsesArray.length) {
        const formGroup = questionResponsesArray.at(questionIndex);
        formGroup.patchValue({
          ratingScale: existingResponse.ratingScale,
          description: existingResponse.description,
          caseBehaviorMappingId: existingResponse.caseBehaviorMappingId,
        });
      }
    });
    if (
      feedbackData.questionResponses &&
      !Array.isArray(feedbackData.questionResponses)
    ) {
      this.totalOverAllScore = feedbackData.overallScore || 0;
    }

    this.pendingFeedbackData = null;

    this.toggleDisable();
  }

  submitFeedback(): void {
    const formValue = { ...this.feedbackForm.value };
    const currentStepData = this.getCurrentStepData();
    if (currentStepData && currentStepData.questions) {
      const currentStepQuestionIds = currentStepData.questions.map(q => q.id);
      formValue.questionResponses =
        this.feedbackForm.value.questionResponses?.filter(
          (response: FeedbackStepAnswer) =>
            currentStepQuestionIds.includes(response.caseFeedbackQuestionId)
        ) || [];
    }

    if (
      formValue.questionResponses &&
      Array.isArray(formValue.questionResponses)
    ) {
      formValue.questionResponses = formValue.questionResponses.map(
        (response: FeedbackStepAnswer) => {
          const { description, ...responseWithoutDescription } = response;
          return {
            ...responseWithoutDescription,
            answerText: description || '',
          };
        }
      );
    }

    this.loaderService.show();
    this.feedbackService
      .submitQuestionFeedback(formValue)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: response => {
          this.loaderService.hide();
          if (response.result) {
            this.feedbackForm.patchValue({
              caseFeedbackSubmissionId:
                response.result.caseFeedbackSubmissionId,
            });
            this.totalOverAllScore = response.result.overallScore || 0;
          }
          if (this.currentStep === 4) {
            this.commonService.notify(
              'success',
              `Feedback submitted successfully`
            );
            this.drawerService.closeDrawer(DRAWER_IDS.FEEDBACK_FORM);
          }
          if (this.currentStep < this.steps.length - 1) {
            this.currentStep++;
          }
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors?.map(
            err => err.detailedMessage || err.description
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  closeCorrectionNeededDrawer() {
    this.drawerService.closeDrawer(DRAWER_IDS.FEEDBACK_FORM);
  }

  nextStep(): void {
    if (this.feedbackDetails.mode === InsightMode.VIEW) {
      if (this.currentStep < this.steps.length - 1) {
        this.currentStep++;
      }
    } else {
      this.submitFeedback();
    }
  }

  previousStep(): void {
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

  goToStep(step: number): void {
    if (step >= 0 && step < this.steps.length) {
      this.currentStep = step;
    }
  }

  getCurrentStepData(): FeedbackStepData | undefined {
    const stepData = this.stepData[this.currentStep];
    return stepData;
  }

  isFirstStep(): boolean {
    return this.currentStep === 0;
  }

  isLastStep(): boolean {
    return this.currentStep === this.steps.length - 1;
  }

  isStepValid(): boolean {
    if (!this.isStepDataInitialized) {
      return false;
    }

    const stepData = this.getCurrentStepData();
    if (!stepData) {
      return false;
    }

    return this.validateCurrentStepForm();
  }

  private validateCurrentStepForm(): boolean {
    const questionResponses = this.feedbackForm.get(
      'questionResponses'
    ) as FormArray;
    const currentStepData = this.getCurrentStepData();

    if (!currentStepData?.questions) {
      return false;
    }

    if (this.currentStep === 1) {
      const behaviorCategoryControl = this.feedbackForm.get(
        'caseBehaviorMappingId'
      );
      const currentValue = behaviorCategoryControl?.value;
      if (!currentValue || currentValue === 0) {
        return false;
      }
    }
    if (this.currentStep === 4) {
      return true;
    }

    return currentStepData.questions.every(question => {
      const questionIndex = this.getQuestionIndex(question.id);
      if (questionIndex >= 0) {
        const control = questionResponses.at(questionIndex);
        const questionType = this.getQuestionType(question);
        const ratingScale = control.get('ratingScale')?.value;
        const description = control.get('description')?.value;

        if (questionType === 'text') {
          if (this.currentStep === 1) {
            return true;
          }
          return description && description.trim() !== '';
        } else {
          // For currentStep === 2, if ratingScale is 3, description is required
          if (this.currentStep === 2 && ratingScale === 3) {
            return description && description.trim() !== '';
          }
          return ratingScale && ratingScale > 0;
        }
      }
      return false;
    });
  }

  private getQuestionIndex(questionId: number): number {
    const questionResponses = this.feedbackForm.get(
      'questionResponses'
    ) as FormArray;
    return questionResponses.controls.findIndex(
      control => control.get('caseFeedbackQuestionId')?.value === questionId
    );
  }

  private getQuestionType(question: FeedbackQuestion): string {
    return QUESTION_TYPE_MAP[question.id] || QuestionType.Scale;
  }

  validateStep(): StepValidation {
    const isValid = this.isStepValid();
    return {
      isValid,
      errors: isValid ? undefined : ['Please complete all required fields.'],
    };
  }

  getStepStatus(stepIndex: number): string {
    if (stepIndex < this.currentStep) {
      return 'finish';
    } else if (stepIndex === this.currentStep) {
      return 'process';
    } else {
      return 'wait';
    }
  }

  async printTemplate() {
    if (this.currentStep === 4) {
      const caseDetail = this.feedbackDetails.caseDetail;
      const selectedTabIndex =
        this.selfAssessmentStepComponent?.selectedTabIndex ?? 0;
      const filteredQuestions =
        this.getFilteredQuestionsForTab(selectedTabIndex);
      const tabNames = ['Teacher', 'Student', 'Both'];
      const factory = this.viewContainerRef.createComponent(
        PrintTemplateComponent,
        {
          injector: this.injector,
        }
      );
      const componentElement = factory.location.nativeElement;
      componentElement.style.display = 'none';
      this.isPrintLoading = true;
      await Promise.all([
        this.orgService
          .getOrganization(caseDetail?.student.organizationId)
          .toPromise(),
      ])
        .then(async ([orgRes]: [OrganizationModel | undefined]) => {
          let base64Image = '';
          if (orgRes && orgRes.logoUrl) {
            base64Image = await this.commonService.getBase64ImageFromUrl(
              orgRes.logoUrl
            );
          } else {
            base64Image = await this.commonService.getBase64ImageFromUrl(
              '/assets/images/logo.png'
            );
          }
          factory.instance.templateData = {
            questionList: filteredQuestions,
            logo: base64Image,
            caseDetail: caseDetail,
            orgDetail: orgRes,
            selectedTab: tabNames[selectedTabIndex] || 'Teacher Only',
          } as PrintTemplateData;
          this.cd.detectChanges();
          this.waitForInitialization(factory).then(() => {
            factory.instance.printPDF();
            factory.instance.templateData = null;
            factory.destroy();
          });
        })
        .catch(e => console.error(e))
        .finally(() => {
          this.isPrintLoading = false;
          this.cd.detectChanges();
        });
    } else {
      return;
    }
  }

  waitForInitialization(factory: ComponentRef<PrintTemplateComponent>) {
    return new Promise<void>(resolve => {
      const interval = setInterval(() => {
        if (factory.instance.initialized) {
          clearInterval(interval);
          resolve();
        }
      }, 10);
    });
  }
}
